import {Currency} from "../../types/Currency";
import {ILatestQuery} from "../../utils/latest_query";
import {ISearchFormValues} from "../types/ISearchFormValues";
import {SearchFormValuesType} from "../types/SearchFormValuesType";
import {formatRecentFilterText} from "./format_recent_filter_text";
import {getSearchUrlWithFilters} from "./get_search_url_with_filters";
import {IRecentFilter} from "./recent_filters_storage";

/** Splits filter text so the first part (city/location) can be shown in bold. */
export const getFilterTextParts = (fullText: string): {boldPart: string; rest: string} => {
    const colonIdx = fullText.indexOf(": ");
    const commaIdx = fullText.indexOf(", ");
    if (colonIdx >= 0 && (commaIdx < 0 || colonIdx < commaIdx)) {
        return {boldPart: fullText.slice(0, colonIdx), rest: fullText.slice(colonIdx)};
    }
    if (commaIdx >= 0) {
        return {boldPart: fullText.slice(0, commaIdx), rest: fullText.slice(commaIdx)};
    }
    return {boldPart: fullText, rest: ""};
};

export interface IRecentFiltersDisplayLinkConfig {
    singleRegionFriendlyLink: (params: {friendlySlug: string; offerListSubFilterSlug?: string}) => string;
    abroadListFriendlyLink: (params: {friendlySlug: string; country: string; abroadRegionSlug?: string}) => string;
    multiRegionLink: () => string;
}

/**
 * Format filter text for survey preferences (used in all recent filter components).
 */
export const getSurveyFilterText = (surveyFormValues: ISearchFormValues): string =>
    formatRecentFilterText({
        filters: {rooms: surveyFormValues.rooms, price: surveyFormValues.price},
        search: surveyFormValues.search,
        currency: Currency.PLN
    }) || "Twoje preferencje";

/**
 * Format filter text for a single recent filter from localStorage.
 */
export const getRecentFilterFilterText = (recentFilter: IRecentFilter): string =>
    formatRecentFilterText({
        filters: recentFilter.filters,
        search: recentFilter.search,
        currency: Currency.PLN
    });

/**
 * Build search URL for survey form values (with latestQuery and link config).
 * Used by RecentFiltersRegions and RecentFiltersSidebar.
 */
export const getSurveySearchUrlWithFilters = (
    params: {
        surveyFormValues: ISearchFormValues;
        latestQuery: ILatestQuery;
    } & IRecentFiltersDisplayLinkConfig
): string =>
    getSearchUrlWithFilters({
        ...params,
        searchFormValues: params.surveyFormValues,
        additionalParams: {sort: params.latestQuery.sort, distance: params.latestQuery.distance}
    }).url;

/**
 * Build search URL for a recent filter (merge with currentFormValues, use latestQuery).
 * Used by RecentFiltersRegions and RecentFiltersSidebar.
 */
export const getRecentFilterSearchUrlWithFilters = (
    params: {
        recentFilter: IRecentFilter;
        currentFormValues: ISearchFormValues;
        latestQuery: ILatestQuery;
    } & IRecentFiltersDisplayLinkConfig
): string => {
    const formValues = {
        ...params.currentFormValues,
        ...params.recentFilter.filters,
        search: params.recentFilter.search
    } as SearchFormValuesType;
    return getSearchUrlWithFilters({
        singleRegionFriendlyLink: params.singleRegionFriendlyLink,
        abroadListFriendlyLink: params.abroadListFriendlyLink,
        multiRegionLink: params.multiRegionLink,
        latestQuery: params.latestQuery,
        searchFormValues: formValues,
        additionalParams: {sort: params.latestQuery.sort, distance: params.latestQuery.distance}
    }).url;
};
